import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Fab,
  TextField,
  InputAdornment,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  ShoppingCart as ShoppingCartIcon,
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Order } from '../../../enums/order';
import { WarehouseService } from '../warehouse/warehouseService';
import OrderTable from './OrderTable';
import OrderDrawer from './OrderDrawer';
import ToastNotification from '../common/Toastnotification';
import { useCompany } from '../../../contexts/CompanyContext';
import { useOrdersPaginated, useOrderStats, useOrderMutations } from '../../../hooks/useOrders';
import { useAuth } from '../../../contexts/AuthContext';

interface OrdersPageProps { }

const ORDERS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();
  const overlayBg =
    theme.palette.mode === 'dark'
      ? 'rgba(26, 29, 36, 0.85)'
      : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'all',
        borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading orders...
        </Typography>
      </Box>
    </Box>
  );
};

const OrdersPage: React.FC<OrdersPageProps> = () => {
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── UI state ──────────────────────────────────────────────────────────────
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit' | 'view'>('add');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<Order | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [warehouseFilter, setWarehouseFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const { hasPermission, isSuperAdmin } = useAuth();

const canView   = isSuperAdmin || hasPermission('orders.view');
const canCreate = isSuperAdmin || hasPermission('orders.create');
const canEdit   = isSuperAdmin || hasPermission('orders.edit');
const canDelete = isSuperAdmin || hasPermission('orders.delete');
const canGenerateInvoice = isSuperAdmin || hasPermission('orders.invoice');

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── React Query hooks ─────────────────────────────────────────────────────
  // `refetch` is pulled out so handleOrderSaved can force a fresh fetch after
  // the drawer saves a new or edited order — exactly the same pattern used in
  // BrandManagement where mutations call queryClient.invalidateQueries() and
  // resetToFirstPage() causes a re-render with page=1.
  //
  // Here we cannot rely solely on resetToFirstPage() because:
  //   • If the user is already on page 1, setting currentPage to 1 is a no-op
  //     and React Query will not refetch (the query key did not change).
  //   • Calling refetch() explicitly tells React Query "this data is stale,
  //     fetch it right now regardless of the cache" — so the table always
  //     shows the latest list immediately after a save.
  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersIsFetching,
    isError: ordersError,
    error: ordersErrorObj,
    refetch: refetchOrders,          // ← NEW: used in handleOrderSaved
  } = useOrdersPaginated({
    page: currentPage,
    search: searchQuery,
    statusFilter,
    paymentStatusFilter,
    warehouseFilter,
    dateFromFilter,
    dateToFilter,
    pageSize: ORDERS_PER_PAGE,
  });

  const { data: statsData, refetch: refetchStats } = useOrderStats(); // ← also refetch stats so counts stay in sync
  const { remove } = useOrderMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const orders = ordersData?.orders ?? [];
  const totalOrders = ordersData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalOrders / ORDERS_PER_PAGE);
  const loading = ordersLoading || ordersIsFetching;

  const stats = statsData ?? {
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    returned: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
  };

  // Load warehouses for the filter dropdown
  React.useEffect(() => {
    WarehouseService.getActiveWarehouses()
      .then(data => setWarehouses(data))
      .catch(err => console.error('Error fetching warehouses:', err));
  }, []);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const handlePaymentStatusFilterChange = (value: string) => {
    setPaymentStatusFilter(value);
    resetToFirstPage();
  };

  const handleWarehouseFilterChange = (value: string) => {
    setWarehouseFilter(value);
    resetToFirstPage();
  };

  const handleDateFromFilterChange = (value: string) => {
    setDateFromFilter(value);
    resetToFirstPage();
  };

  const handleDateToFilterChange = (value: string) => {
    setDateToFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setPaymentStatusFilter('all');
    setWarehouseFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    resetToFirstPage();
  };

  const hasActiveFilters =
    searchQuery !== '' ||
    statusFilter !== 'all' ||
    paymentStatusFilter !== 'all' ||
    warehouseFilter !== 'all' ||
    dateFromFilter !== '' ||
    dateToFilter !== '';

  const activeFiltersCount = [
    searchQuery !== '',
    statusFilter !== 'all',
    paymentStatusFilter !== 'all',
    warehouseFilter !== 'all',
    dateFromFilter !== '',
    dateToFilter !== '',
  ].filter(Boolean).length;

  // ── Drawer handlers ───────────────────────────────────────────────────────
  const handleAddOrder = () => { setSelectedOrder(null); setDrawerMode('add'); setIsDrawerOpen(true); };
  const handleEditOrder = (order: Order) => { setSelectedOrder(order); setDrawerMode('edit'); setIsDrawerOpen(true); };
  const handleViewOrder = (order: Order) => { setSelectedOrder(order); setDrawerMode('view'); setIsDrawerOpen(true); };
  const handleDrawerClose = () => { setIsDrawerOpen(false); setSelectedOrder(null); };

  // ── handleOrderSaved — the key refresh logic ──────────────────────────────
  //
  // Called by OrderDrawer after a successful create or update.
  //
  // Why both resetToFirstPage() AND refetchOrders()?
  //
  //   resetToFirstPage() navigates back to page 1 so the user sees the newest
  //   order at the top. But if the user was already on page 1 when they edited
  //   an existing order, setCurrentPage(1) is a no-op — React Query sees the
  //   same query key and serves the stale cache, so the edited row would NOT
  //   update on screen.
  //
  //   refetchOrders() solves this: it bypasses the cache and fetches fresh data
  //   for the current query key unconditionally. Together the two calls cover
  //   both scenarios:
  //     • New order added on page 3  → page resets to 1 → query key changes →
  //       React Query fetches page 1 (refetchOrders is also called but the
  //       page-change fetch is what matters here).
  //     • Order edited while on page 1 → page stays 1 → query key unchanged →
  //       refetchOrders() forces the fetch that would otherwise be skipped.
  //
  //   refetchStats() keeps the summary cards (Total, Pending, Revenue, etc.)
  //   in sync after every save without needing a full page reload.
  // ─────────────────────────────────────────────────────────────────────────
  const handleOrderSaved = () => {
    handleDrawerClose();
    resetToFirstPage();   // navigate to page 1 so the new/edited order is visible
    refetchOrders();      // force fresh fetch even if page didn't change
    refetchStats();       // keep stat cards in sync
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteOrder = (order: Order) => setDeleteTarget(order);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await remove.mutateAsync(deleteTarget.id!);
      setDeleteTarget(null);
      // After delete: reset to page 1 + force refetch + update stats
      resetToFirstPage();
      refetchOrders();
      refetchStats();
      showToast('Order deleted successfully', 'success');
    } catch (err: any) {
      showToast('Failed to delete order: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleRefresh = () => {
    resetToFirstPage();
    refetchOrders();
    refetchStats();
    showToast('Orders refreshed', 'info');
  };

  const formatPrice = (price: number) => {
    const currency = companyProfile?.currency || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
  };

  if (!canView) {
  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <ShoppingCartIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
          <Typography variant="h5" gutterBottom>Access Denied</Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to view orders. Please contact your administrator.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && orders.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1">Loading orders...</Typography>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <ShoppingCartIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Orders
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage customer orders and fulfillment
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' }, alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
            <Button
              onClick={handleRefresh}
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              Refresh
            </Button>
            {canCreate && (
    <Button onClick={handleAddOrder} variant="contained" startIcon={<AddIcon />}>
      Add Order
    </Button>
  )}
          </Stack>
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }}>
          {[
            { label: 'Total Orders', value: stats.total, caption: 'All time' },
            { label: 'Pending', value: stats.pending, caption: 'Awaiting processing' },
            { label: 'Processing', value: stats.processing, caption: 'In fulfillment' },
            { label: 'Delivered', value: stats.delivered, caption: 'Completed orders' },
            { label: 'Revenue', value: formatPrice(stats.totalRevenue), caption: 'Total revenue' },
          ].map((stat) => (
            <Grid item xs={6} sm={6} md={2.4} key={stat.label}>
              <Card>
                <CardContent
                  sx={{
                    textAlign: 'center',
                    p: { xs: 1, sm: 1.25 },
                    '&:last-child': { pb: { xs: 1, sm: 1.25 } },
                  }}
                >
                  <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    component="div"
                    color="text.primary"
                    sx={{ fontSize: { xs: '0.85rem', sm: '1.05rem', md: '1.3rem' } }}
                    noWrap
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                  >
                    {stat.caption}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by order #, customer, tracking... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Order Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Order Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="confirmed">Confirmed</MenuItem>
                      <MenuItem value="processing">Processing</MenuItem>
                      <MenuItem value="shipped">Shipped</MenuItem>
                      <MenuItem value="delivered">Delivered</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="returned">Returned</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={paymentStatusFilter}
                      onChange={(e) => handlePaymentStatusFilterChange(e.target.value)}
                      label="Payment Status"
                    >
                      <MenuItem value="all">All Payments</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="refunded">Refunded</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {/* Warehouse filter — uncomment when needed
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Warehouse</InputLabel>
                    <Select
                      value={warehouseFilter}
                      onChange={(e) => handleWarehouseFilterChange(e.target.value)}
                      label="Warehouse"
                    >
                      <MenuItem value="all">All Warehouses</MenuItem>
                      {warehouses.map((warehouse) => (
                        <MenuItem key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="From Date"
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => handleDateFromFilterChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="To Date"
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => handleDateToFilterChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${orders.length} orders matching your filters`
                        : `Showing ${orders.length} orders on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {orders.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Orders Match Your Filters' : 'No Orders Found'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                >
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No orders available yet. Create your first order to get started.'}
                </Typography>
                {hasActiveFilters ? (
  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
    Clear Filters
  </Button>
) : canCreate && (
  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOrder}>
    Add Order
  </Button>
)}
              </CardContent>
            </Card>
          </Box>
        )}

        {orders.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'auto', md: 0 },
            }}
          >
            <Box
              ref={tableWrapperRef}
              sx={{ flex: 1, overflow: 'auto', mb: 2, minHeight: { xs: 'auto', md: 0 } }}
            >
              <OrderTable
                orders={orders}
                onView={handleViewOrder}
                onEdit={handleEditOrder}
                onDelete={handleDeleteOrder}
                deleteLoading={deleteLoading}
                isDark={isDark}
                canEdit={canEdit}      // ADD
  canDelete={canDelete}
  canGenerateInvoice={canGenerateInvoice}
              />
            </Box>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalOrders === 0 ? 0 : (currentPage - 1) * ORDERS_PER_PAGE + 1}–
                    {Math.min(currentPage * ORDERS_PER_PAGE, totalOrders)} of {totalOrders} orders
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Drawer ── */}
      <OrderDrawer
        isOpen={isDrawerOpen}
        mode={drawerMode}
        order={selectedOrder}
        onClose={handleDrawerClose}
        onSave={handleOrderSaved}
        onShowToast={showToast}
      />

      {/* ── Delete Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete order{' '}
            <strong>"{deleteTarget?.orderNumber}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />

      {/* ── Mobile FAB ── */}
      {canCreate && (
  <Fab
    color="primary"
    aria-label="add order"
    // Sits above the globally-mounted AdminAIChat FAB (also bottom:24, right:24)
    // so the two don't overlap/hide each other on mobile.
    sx={{ position: 'fixed', bottom: 96, right: 24, display: { xs: 'flex', md: 'none' } }}
    onClick={handleAddOrder}
  >
    <AddIcon />
  </Fab>
)}
    </Box>
  );
};

export default OrdersPage;